import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { 
    Plus, 
    Search, 
    Edit2, 
    Trash2, 
    Users, 
    AlertCircle, 
    CheckCircle2, 
    X, 
    Upload, 
    ChevronDown, 
    ChevronUp,
    Image as ImageIcon,
    Loader2,
    Settings,
    FileText,
    Globe
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { uploadImage } from '../../lib/storage';
import { MOCK_TEAM, TeamMember } from '../../data/team';

const TeamManager: React.FC = () => {
    const { theme } = useOutletContext<{ theme: 'light' | 'dark' }>();
    const isDark = theme === 'dark';

    const labelClass = `text-[10px] font-bold uppercase tracking-widest block transition-colors duration-200 ${
        isDark ? 'text-gray-400' : 'text-gray-600'
    }`;
    const inputClass = `w-full border px-4 py-3 text-xs outline-none focus:border-[#00d4ff] focus:ring-2 focus:ring-[#00d4ff]/10 transition-all duration-200 rounded-[8px] ${
        isDark ? 'bg-black border-rock text-white placeholder-gray-600' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 shadow-sm'
    }`;
    const selectClass = `w-full border px-4 py-3 text-xs outline-none focus:border-[#00d4ff] focus:ring-2 focus:ring-[#00d4ff]/10 transition-all duration-200 rounded-[8px] ${
        isDark ? 'bg-black border-rock text-white' : 'bg-white border-gray-200 text-gray-900 shadow-sm'
    }`;
    const inputClassSm = `w-full border px-3 py-2.5 text-xs outline-none focus:border-[#00d4ff] focus:ring-1 focus:ring-[#00d4ff]/20 transition-all duration-200 rounded-[6px] ${
        isDark ? 'bg-black border-rock text-white placeholder-gray-600' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 shadow-sm'
    }`;

    const [team, setTeam] = useState<TeamMember[]>([]);
    const [search, setSearch] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<'all' | 'ceo' | 'guide'>('all');
    const [notification, setNotification] = useState<{ message: string, type: 'error' | 'success' } | null>(null);
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
    
    // Form State
    const [formName, setFormName] = useState('');
    const [formCategory, setFormCategory] = useState<'ceo' | 'guide'>('ceo');
    const [formImage, setFormImage] = useState('');
    const [formOrderIndex, setFormOrderIndex] = useState(0);
    const [formRoleEs, setFormRoleEs] = useState('');
    const [formRoleEn, setFormRoleEn] = useState('');
    const [formBioEs, setFormBioEs] = useState('');
    const [formBioEn, setFormBioEn] = useState('');
    const [formSpecialtyEs, setFormSpecialtyEs] = useState('');
    const [formSpecialtyEn, setFormSpecialtyEn] = useState('');
    const [formFavoriteRouteEs, setFormFavoriteRouteEs] = useState('');
    const [formFavoriteRouteEn, setFormFavoriteRouteEn] = useState('');
    const [activeFormTab, setActiveFormTab] = useState<'es' | 'en' | 'others'>('es');
    
    // Additional Languages (Collapsible)
    const [showAdditionalLangs, setShowAdditionalLangs] = useState(false);
    const [formRolePt, setFormRolePt] = useState('');
    const [formRoleFr, setFormRoleFr] = useState('');
    const [formRoleIt, setFormRoleIt] = useState('');
    const [formRoleDe, setFormRoleDe] = useState('');
    const [formBioPt, setFormBioPt] = useState('');
    const [formBioFr, setFormBioFr] = useState('');
    const [formBioIt, setFormBioIt] = useState('');
    const [formBioDe, setFormBioDe] = useState('');

    const [isUploading, setIsUploading] = useState(false);
    const [customConfirm, setCustomConfirm] = useState<{
        message: string;
        onConfirm: () => void;
    } | null>(null);

    // Auto-hide notification
    useEffect(() => {
        if (notification) {
            const timer = setTimeout(() => setNotification(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [notification]);

    // Prevent body scroll when editor modal is open
    useEffect(() => {
        if (isEditorOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isEditorOpen]);

    // Fetch team
    const fetchTeam = async () => {
        try {
            const { data, error } = await supabase
                .from('team')
                .select('*')
                .order('order_index', { ascending: true });
            
            if (error) throw error;
            if (data && data.length > 0) {
                setTeam(data);
            } else {
                setTeam(MOCK_TEAM);
            }
        } catch (err: any) {
            console.error('Error fetching team from Supabase, using mock fallback:', err);
            setTeam(MOCK_TEAM);
        }
    };

    useEffect(() => {
        fetchTeam();
    }, []);

    const openEditor = (member: TeamMember | null = null) => {
        if (member) {
            setEditingMember(member);
            setFormName(member.name);
            setFormCategory(member.category);
            setFormImage(member.image);
            setFormOrderIndex(member.order_index);
            setFormRoleEs(member.role_es);
            setFormRoleEn(member.role_en);
            setFormBioEs(member.bio_es);
            setFormBioEn(member.bio_en);
            setFormSpecialtyEs(member.specialty_es || '');
            setFormSpecialtyEn(member.specialty_en || '');
            setFormFavoriteRouteEs(member.favorite_route_es || '');
            setFormFavoriteRouteEn(member.favorite_route_en || '');
            // Additional translations
            setFormRolePt(member.role_pt || '');
            setFormRoleFr(member.role_fr || '');
            setFormRoleIt(member.role_it || '');
            setFormRoleDe(member.role_de || '');
            setFormBioPt(member.bio_pt || '');
            setFormBioFr(member.bio_fr || '');
            setFormBioIt(member.bio_it || '');
            setFormBioDe(member.bio_de || '');
        } else {
            setEditingMember(null);
            setFormName('');
            setFormCategory('guide');
            setFormImage('');
            // Next order index
            const maxOrder = team.reduce((max, m) => m.order_index > max ? m.order_index : max, 0);
            setFormOrderIndex(maxOrder + 1);
            setFormRoleEs('');
            setFormRoleEn('');
            setFormBioEs('');
            setFormBioEn('');
            setFormSpecialtyEs('');
            setFormSpecialtyEn('');
            setFormFavoriteRouteEs('');
            setFormFavoriteRouteEn('');
            // Reset translations
            setFormRolePt('');
            setFormRoleFr('');
            setFormRoleIt('');
            setFormRoleDe('');
            setFormBioPt('');
            setFormBioFr('');
            setFormBioIt('');
            setFormBioDe('');
        }
        setActiveFormTab('es');
        setIsEditorOpen(true);
    };

    const handleUploadImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploading(true);
        try {
            // Upload to default bucket 'expeditions' in folder 'team'
            const url = await uploadImage(file, 'expeditions', 'team');
            setFormImage(url);
            setNotification({ message: 'Imagen subida correctamente', type: 'success' });
        } catch (err: any) {
            console.error('Error uploading image to Supabase:', err);
            setNotification({ message: `Error al subir imagen: ${err.message || err}`, type: 'error' });
        } finally {
            setIsUploading(false);
            if (e.target) e.target.value = '';
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formName || !formRoleEs || !formRoleEn || !formBioEs || !formBioEn) {
            setNotification({ message: 'Completa todos los campos obligatorios.', type: 'error' });
            return;
        }

        const id = editingMember ? editingMember.id : undefined;

        const dbPayload: Partial<TeamMember> = {
            name: formName,
            category: formCategory,
            image: formImage || 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&q=80&w=600&h=600', // fallback mountain
            role_es: formRoleEs,
            role_en: formRoleEn,
            bio_es: formBioEs,
            bio_en: formBioEn,
            order_index: Number(formOrderIndex),
            role_pt: formRolePt || null as any,
            role_fr: formRoleFr || null as any,
            role_it: formRoleIt || null as any,
            role_de: formRoleDe || null as any,
            bio_pt: formBioPt || null as any,
            bio_fr: formBioFr || null as any,
            bio_it: formBioIt || null as any,
            bio_de: formBioDe || null as any,
            specialty_es: formSpecialtyEs || null as any,
            specialty_en: formSpecialtyEn || null as any,
            favorite_route_es: formFavoriteRouteEs || null as any,
            favorite_route_en: formFavoriteRouteEn || null as any,
        };

        if (id) {
            dbPayload.id = id;
        }

        try {
            const { error } = await supabase
                .from('team')
                .upsert(dbPayload);

            if (error) throw error;

            setNotification({ message: 'Miembro del equipo guardado con éxito', type: 'success' });
            setIsEditorOpen(false);
            fetchTeam();
        } catch (err: any) {
            console.error('Error saving to Supabase team table:', err);
            // Local fallback simulation (just modify mock state to make it testable immediately in frontend)
            if (editingMember) {
                setTeam(prev => prev.map(m => m.id === editingMember.id ? { ...m, ...dbPayload } as TeamMember : m));
            } else {
                const tempId = `team-${Math.random().toString(36).substring(2, 9)}`;
                setTeam(prev => [...prev, { ...dbPayload, id: tempId } as TeamMember]);
            }
            setNotification({ 
                message: `Error al guardar en base de datos. Guardado solo en memoria local (${err.message || 'La tabla "team" aún no existe en Supabase'}).`, 
                type: 'error' 
            });
            setIsEditorOpen(false);
        }
    };

    const handleDeleteClick = (member: TeamMember) => {
        setCustomConfirm({
            message: `¿Estás seguro/a de que deseas eliminar a "${member.name}"? Esta acción se borrará permanentemente de Supabase.`,
            onConfirm: async () => {
                try {
                    const { error } = await supabase
                        .from('team')
                        .delete()
                        .eq('id', member.id);

                    if (error) throw error;

                    setNotification({ message: 'Miembro eliminado correctamente', type: 'success' });
                    fetchTeam();
                } catch (err: any) {
                    console.error('Error deleting team member from Supabase:', err);
                    // Local fallback
                    setTeam(prev => prev.filter(m => m.id !== member.id));
                    setNotification({ message: 'Error al eliminar en base de datos. Eliminado de la memoria local.', type: 'error' });
                }
            }
        });
    };

    const filteredTeam = team
        .filter(m => selectedCategory === 'all' || m.category === selectedCategory)
        .filter(m => m.name.toLowerCase().includes(search.toLowerCase()))
        .sort((a, b) => a.order_index - b.order_index);

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Header Actions */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                    <h1 className={`text-4xl font-title uppercase tracking-tighter ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Equipo & Staff
                    </h1>
                    <p className="text-gray-400 text-[10px] uppercase tracking-[0.3em] font-bold mt-1">
                        Administra los CEOs y Guías que aparecen en la página Nosotros.
                    </p>
                </div>
                
                <button 
                    onClick={() => openEditor(null)}
                    className="bg-[#00d4ff] text-black px-8 py-4 font-title text-lg uppercase tracking-widest flex items-center gap-3 hover:bg-white transition-all shadow-[0_10px_30px_-10px_rgba(0,212,255,0.4)] rounded-[10px]"
                >
                    <Plus className="w-5 h-5" />
                    Nuevo Miembro
                </button>
            </div>

            {/* Filters Bar */}
            <div className={`flex flex-col lg:flex-row gap-4 items-center justify-between border p-4 rounded-[10px] transition-colors ${
                isDark ? 'bg-[#1a1512] border-rock' : 'bg-white border-gray-200 shadow-sm'
            }`}>
                <div className="flex items-center gap-2 overflow-x-auto w-full lg:w-auto scrollbar-hide">
                    <button 
                        onClick={() => setSelectedCategory('all')}
                        className={`px-4 py-2 text-[10px] font-bold uppercase tracking-widest whitespace-nowrap rounded-[10px] transition-all ${
                            selectedCategory === 'all'
                            ? 'bg-[#00d4ff] text-black'
                            : isDark ? 'bg-black border border-rock text-gray-400 hover:border-[#00d4ff] hover:text-white' : 'bg-gray-50 border border-gray-200 text-gray-500 hover:border-[#00d4ff] hover:text-gray-900'
                        }`}
                    >
                        Todos
                    </button>
                    <button 
                        onClick={() => setSelectedCategory('ceo')}
                        className={`px-4 py-2 border text-[10px] font-bold uppercase tracking-widest whitespace-nowrap transition-all rounded-[10px] ${
                            selectedCategory === 'ceo'
                            ? 'bg-[#00d4ff] text-black border-[#00d4ff]'
                            : isDark ? 'bg-black border-rock text-gray-400 hover:border-[#00d4ff] hover:text-white' : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-[#00d4ff] hover:text-gray-900'
                        }`}
                    >
                        Directores (CEOs)
                    </button>
                    <button 
                        onClick={() => setSelectedCategory('guide')}
                        className={`px-4 py-2 border text-[10px] font-bold uppercase tracking-widest whitespace-nowrap transition-all rounded-[10px] ${
                            selectedCategory === 'guide'
                            ? 'bg-[#00d4ff] text-black border-[#00d4ff]'
                            : isDark ? 'bg-black border-rock text-gray-400 hover:border-[#00d4ff] hover:text-white' : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-[#00d4ff] hover:text-gray-900'
                        }`}
                    >
                        Guías Certificados
                    </button>
                </div>

                <div className="flex items-center gap-4 w-full lg:w-auto">
                    <div className="relative flex-1 lg:w-64">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <input 
                            type="text" 
                            placeholder="Buscar miembro..." 
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className={`w-full border pl-11 pr-4 py-3 text-xs outline-none focus:border-[#00d4ff] transition-all rounded-[10px] ${
                                isDark ? 'bg-black border-rock text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                            }`}
                        />
                    </div>
                </div>
            </div>

            {/* List Table */}
            <div className="grid grid-cols-1 gap-4">
                {filteredTeam.map((member) => (
                    <div key={member.id} className={`border group transition-all duration-300 rounded-[10px] overflow-hidden ${
                        isDark ? 'bg-[#1a1512] border-rock hover:border-[#00d4ff]/40' : 'bg-white border-gray-200 hover:border-[#00d4ff] shadow-sm'
                    }`}>
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between p-6 gap-6">
                            {/* Image, Name, Role */}
                            <div className="flex items-center gap-6 flex-1">
                                <div className={`w-16 h-16 shrink-0 border overflow-hidden rounded-[8px] ${isDark ? 'border-rock bg-black' : 'border-gray-100 bg-gray-50'}`}>
                                    <img src={member.image} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" alt={member.name} />
                                </div>
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className={`text-[8px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-[10px] ${
                                            member.category === 'ceo' ? 'bg-[#00d4ff]/10 text-[#00d4ff]' : 'bg-[#25D366]/10 text-[#25D366]'
                                        }`}>
                                            {member.category === 'ceo' ? 'CEO / Director' : 'Guía / Staff'}
                                        </span>
                                        <span className="text-[8px] font-bold text-gray-500 uppercase tracking-widest">Orden: {member.order_index}</span>
                                    </div>
                                    <h3 className={`text-lg font-title uppercase tracking-tight truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                        {member.name}
                                    </h3>
                                    <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">
                                        ES: {member.role_es} • EN: {member.role_en}
                                    </p>
                                </div>
                            </div>

                            {/* Bio Brief */}
                            <div className="flex-1 max-w-md lg:block hidden">
                                <p className="text-xs text-gray-400 font-light line-clamp-2 leading-relaxed">
                                    {member.bio_es}
                                </p>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-3 justify-end lg:w-44 border-t pt-4 lg:border-t-0 lg:pt-0">
                                <button 
                                    onClick={() => openEditor(member)}
                                    className={`flex-1 lg:flex-none flex items-center justify-center gap-2 px-5 py-3 border transition-all rounded-[10px] ${
                                        isDark ? 'bg-black border-rock text-gray-400 hover:text-[#00d4ff] hover:border-[#00d4ff]/50' : 'bg-gray-50 border-gray-200 text-gray-500 hover:text-[#00d4ff] hover:text-[#00d4ff]'
                                    }`}
                                >
                                    <Edit2 className="w-4 h-4" />
                                    <span className="text-[10px] font-bold uppercase tracking-widest">Editar</span>
                                </button>
                                <button 
                                    onClick={() => handleDeleteClick(member)}
                                    className={`p-3 border transition-all rounded-[10px] ${
                                        isDark ? 'bg-black border-rock text-gray-600 hover:text-red-500 hover:border-red-500/50' : 'bg-gray-50 border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-500'
                                    }`}
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}

                {filteredTeam.length === 0 && (
                    <div className="text-center py-20 opacity-50 uppercase tracking-widest text-xs font-bold">
                        No se encontraron miembros del equipo.
                    </div>
                )}
            </div>

            {/* Notification Toast */}
            {notification && (
                <div className="fixed top-10 left-1/2 -translate-x-1/2 z-[200000] animate-in fade-in slide-in-from-top duration-500">
                    <div className={`px-8 py-5 flex items-center gap-4 shadow-2xl border-2 backdrop-blur-xl rounded-[10px] ${
                        notification.type === 'error' ? 'bg-red-600 border-red-400 text-white' : 'bg-[#25D366] border-[#25D366]/50 text-white'
                    }`}>
                        <div className="bg-white/20 p-2 rounded-full">
                            {notification.type === 'error' ? <AlertCircle className="w-6 h-6" /> : <CheckCircle2 className="w-6 h-6" />}
                        </div>
                        <div>
                            <p className="text-[12px] font-black uppercase tracking-widest">{notification.message}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Dialog */}
            {customConfirm && createPortal(
                <div className="fixed inset-0 z-[200000] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className={`w-full max-w-sm p-8 shadow-[0_40px_100px_rgba(0,0,0,0.7)] rounded-[10px] border animate-in zoom-in-95 duration-300 ${
                        isDark ? 'bg-[#1a1512] border-rock text-white' : 'bg-white border-gray-100 text-gray-900'
                    }`}>
                        <div className="flex flex-col items-center text-center space-y-6">
                            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center">
                                <AlertCircle className="w-8 h-8 text-red-500" />
                            </div>
                            <div className="space-y-2">
                                <h3 className="text-xl font-title uppercase tracking-tight">¿Confirmar acción?</h3>
                                <p className="text-[11px] opacity-70 font-bold uppercase tracking-widest leading-relaxed">
                                    {customConfirm.message}
                                </p>
                            </div>
                            <div className="flex flex-col w-full gap-3 pt-4">
                                <button 
                                    onClick={() => {
                                        customConfirm.onConfirm();
                                        setCustomConfirm(null);
                                    }}
                                    className="bg-red-500 text-white py-4 font-title text-sm uppercase tracking-widest hover:bg-red-600 transition-all rounded-[10px] shadow-lg shadow-red-500/20"
                                >
                                    Confirmar
                                </button>
                                <button 
                                    onClick={() => setCustomConfirm(null)}
                                    className={`py-4 text-[10px] font-black uppercase tracking-widest transition-all rounded-[10px] ${
                                        isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-100'
                                    }`}
                                >
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Team Member Editor Overlay */}
            {isEditorOpen && createPortal(
                <div className="fixed inset-0 z-[100000] flex justify-end bg-black/60 backdrop-blur-sm animate-in fade-in duration-500">
                    <div className={`w-full max-w-2xl h-full flex flex-col shadow-2xl border-l animate-in slide-in-from-right duration-500 ${
                        isDark ? 'bg-[#1a1512] border-rock text-white' : 'bg-white border-gray-100 text-gray-900'
                    }`}>
                        {/* Editor Header */}
                        <div className={`p-6 border-b flex items-center justify-between shrink-0 ${isDark ? 'border-rock/60 bg-[#1a1512]' : 'border-gray-200/80 bg-gray-50'}`}>
                            <div>
                                <h2 className="text-2xl font-title uppercase tracking-tight">
                                    {editingMember ? 'Editar Miembro' : 'Nuevo Miembro'}
                                </h2>
                                <p className={`text-[9px] uppercase tracking-widest font-bold mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {editingMember ? `Modificando perfil de ${editingMember.name}` : 'Añadir fundador o guía'}
                                </p>
                            </div>
                            <button 
                                type="button"
                                onClick={() => setIsEditorOpen(false)}
                                className={`p-2 rounded-full transition-all duration-200 ${isDark ? 'hover:bg-white/5 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-900'}`}
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Tab Navigation (Sticky / Non-scrollable) */}
                        <div className={`px-8 pt-6 pb-2 border-b shrink-0 ${
                            isDark ? 'border-rock/30 bg-[#1a1512]' : 'border-gray-150 bg-gray-50'
                        }`}>
                            <div className={`flex gap-1.5 p-1.5 rounded-[12px] ${
                                isDark ? 'bg-black/40 border border-rock/40' : 'bg-gray-100 border border-gray-200 bg-white'
                            }`}>
                                <button 
                                    type="button"
                                    onClick={() => setActiveFormTab('es')}
                                    className={`flex-1 flex items-center justify-center gap-1.5 py-3.5 px-2 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider rounded-[10px] transition-all duration-300 ${
                                        activeFormTab === 'es' 
                                        ? 'bg-[#00d4ff] text-black shadow-md shadow-[#00d4ff]/20' 
                                        : isDark 
                                          ? 'text-gray-400 hover:text-white hover:bg-white/5' 
                                          : 'text-gray-600 hover:text-gray-900 hover:bg-white shadow-sm'
                                    }`}
                                >
                                    <FileText className="w-3.5 h-3.5" />
                                    <span>Español (ES)</span>
                                </button>
                                <button 
                                    type="button"
                                    onClick={() => setActiveFormTab('en')}
                                    className={`flex-1 flex items-center justify-center gap-1.5 py-3.5 px-2 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider rounded-[10px] transition-all duration-300 ${
                                        activeFormTab === 'en' 
                                        ? 'bg-[#00d4ff] text-black shadow-md shadow-[#00d4ff]/20' 
                                        : isDark 
                                          ? 'text-gray-400 hover:text-white hover:bg-white/5' 
                                          : 'text-gray-600 hover:text-gray-900 hover:bg-white shadow-sm'
                                    }`}
                                >
                                    <Globe className="w-3.5 h-3.5" />
                                    <span>Inglés (EN)</span>
                                </button>
                                <button 
                                    type="button"
                                    onClick={() => setActiveFormTab('others')}
                                    className={`flex-1 flex items-center justify-center gap-1.5 py-3.5 px-2 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider rounded-[10px] transition-all duration-300 ${
                                        activeFormTab === 'others' 
                                        ? 'bg-[#00d4ff] text-black shadow-md shadow-[#00d4ff]/20' 
                                        : isDark 
                                          ? 'text-gray-400 hover:text-white hover:bg-white/5' 
                                          : 'text-gray-600 hover:text-gray-900 hover:bg-white shadow-sm'
                                    }`}
                                >
                                    <Plus className="w-3.5 h-3.5" />
                                    <span>Otros (PT/FR/...)</span>
                                </button>
                            </div>
                        </div>

                        {/* Editor Form Body */}
                        <form id="member-form" onSubmit={handleSave} className="flex-1 overflow-y-auto p-8 space-y-8 min-h-0 pb-12">
                            {/* Tab Content */}
                            {activeFormTab === 'es' && (
                                <div className="space-y-6 animate-in fade-in duration-300">
                                    {/* Section: Información Básica */}
                                    <div className={`p-6 border rounded-[12px] space-y-6 ${
                                        isDark ? 'bg-black/40 border-rock/50 shadow-inner' : 'bg-gray-50 border-gray-200/90 shadow-sm'
                                    }`}>
                                        <div className={`flex items-center gap-2 pb-3 border-b ${
                                            isDark ? 'border-rock/20' : 'border-gray-150/50'
                                        }`}>
                                            <span className="text-[9px] font-black uppercase tracking-widest text-[#00d4ff]">1. Información Básica</span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-6">
                                            <div className="space-y-2">
                                                <label className={labelClass}>Nombre Completo *</label>
                                                <input 
                                                    type="text" 
                                                    required
                                                    value={formName}
                                                    onChange={e => setFormName(e.target.value)}
                                                    className={inputClass}
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <label className={labelClass}>Categoría *</label>
                                                <select 
                                                    value={formCategory}
                                                    onChange={e => setFormCategory(e.target.value as 'ceo' | 'guide')}
                                                    className={selectClass}
                                                >
                                                    <option value="ceo">CEO / Director</option>
                                                    <option value="guide">Guía Certificado</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-6">
                                            <div className="space-y-2">
                                                <label className={labelClass}>Orden de Visualización *</label>
                                                <input 
                                                    type="number" 
                                                    required
                                                    value={formOrderIndex}
                                                    onChange={e => setFormOrderIndex(Number(e.target.value))}
                                                    className={inputClass}
                                                />
                                            </div>
                                            <div className="space-y-2 opacity-60 select-none">
                                                <label className={labelClass}>Identificador (ID)</label>
                                                <input 
                                                    type="text" 
                                                    disabled
                                                    placeholder="Autogenerado al guardar"
                                                    value={editingMember ? editingMember.id : ''}
                                                    className={`${inputClass} cursor-not-allowed`}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Section: Foto de Perfil */}
                                    <div className={`p-6 border rounded-[12px] space-y-6 ${
                                        isDark ? 'bg-black/40 border-rock/50 shadow-inner' : 'bg-gray-50 border-gray-200/90 shadow-sm'
                                    }`}>
                                        <div className={`flex items-center gap-2 pb-3 border-b ${
                                            isDark ? 'border-rock/20' : 'border-gray-150/50'
                                        }`}>
                                            <span className="text-[9px] font-black uppercase tracking-widest text-[#00d4ff]">2. Foto de Perfil</span>
                                        </div>
                                        <div className="space-y-2">
                                            <label className={labelClass}>Foto de Perfil</label>
                                            <div className="flex gap-2">
                                                <input 
                                                    type="text" 
                                                    placeholder="URL de la imagen..."
                                                    value={formImage}
                                                    onChange={e => setFormImage(e.target.value)}
                                                    className={inputClass}
                                                />
                                                <label className={`px-5 py-3 border rounded-[8px] cursor-pointer flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-widest transition-all duration-200 shrink-0 ${
                                                    isUploading 
                                                    ? 'opacity-50 pointer-events-none' 
                                                    : isDark 
                                                      ? 'bg-black/60 border-rock text-gray-400 hover:text-white hover:bg-white/5 hover:border-white' 
                                                      : 'bg-white border-gray-200 text-gray-600 hover:text-gray-900 hover:bg-gray-50 hover:border-gray-300 shadow-sm'
                                                }`}>
                                                    {isUploading ? (
                                                        <Loader2 className="w-4 h-4 animate-spin text-[#00d4ff]" />
                                                    ) : (
                                                        <Upload className="w-4 h-4" />
                                                    )}
                                                    <span>Subir</span>
                                                    <input 
                                                        type="file" 
                                                        accept="image/*" 
                                                        onChange={handleUploadImage}
                                                        className="hidden" 
                                                        disabled={isUploading}
                                                    />
                                                </label>
                                            </div>
                                        </div>

                                        {/* Preview image if set */}
                                        {formImage && (
                                            <div className={`p-4 border rounded-[10px] flex items-center justify-between gap-4 transition-colors duration-200 ${
                                                isDark ? 'border-rock/40 bg-black/60' : 'border-gray-200 bg-white shadow-sm'
                                            }`}>
                                                <div className="flex items-center gap-4 min-w-0">
                                                    <div className="relative group w-16 h-16 rounded-[8px] border overflow-hidden shrink-0 shadow-sm">
                                                        <img src={formImage} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" alt="Vista previa" />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="text-[9px] font-bold text-[#00d4ff] uppercase tracking-widest mb-0.5">Previsualización de Foto</p>
                                                        <p className={`text-[10px] truncate max-w-[320px] font-mono ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{formImage}</p>
                                                    </div>
                                                </div>
                                                <button 
                                                    type="button" 
                                                    onClick={() => setFormImage('')}
                                                    className={`p-2 rounded-full border transition-all duration-200 ${
                                                        isDark 
                                                        ? 'border-rock hover:bg-red-500/10 text-gray-400 hover:text-red-400' 
                                                        : 'border-gray-200 hover:bg-red-50 text-gray-500 hover:text-red-500'
                                                    }`}
                                                    title="Eliminar imagen"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Section: Detalles de Montaña (ES) */}
                                    <div className={`p-6 border rounded-[12px] space-y-6 ${
                                        isDark ? 'bg-black/40 border-rock/50 shadow-inner' : 'bg-gray-50 border-gray-200/90 shadow-sm'
                                    }`}>
                                        <div className={`flex items-center gap-2 pb-3 border-b ${
                                            isDark ? 'border-rock/20' : 'border-gray-150/50'
                                        }`}>
                                            <span className="text-[9px] font-black uppercase tracking-widest text-[#00d4ff]">3. Detalles de Montaña / Foco & Ruta (ES)</span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-6">
                                            <div className="space-y-2">
                                                <label className={labelClass}>Foco / Especialidad (ES)</label>
                                                <input 
                                                    type="text" 
                                                    placeholder="ej. Escalada tradicional en fisuras"
                                                    value={formSpecialtyEs}
                                                    onChange={e => setFormSpecialtyEs(e.target.value)}
                                                    className={inputClass}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label className={labelClass}>Ruta Favorita (ES)</label>
                                                <input 
                                                    type="text" 
                                                    placeholder="ej. Pared Norte de Cerro Castillo"
                                                    value={formFavoriteRouteEs}
                                                    onChange={e => setFormFavoriteRouteEs(e.target.value)}
                                                    className={inputClass}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Section: Perfil en Español */}
                                    <div className={`p-6 border rounded-[12px] space-y-6 ${
                                        isDark ? 'bg-black/40 border-rock/50 shadow-inner' : 'bg-gray-50 border-gray-200/90 shadow-sm'
                                    }`}>
                                        <div className={`flex items-center gap-2 pb-3 border-b ${
                                            isDark ? 'border-rock/20' : 'border-gray-150/50'
                                        }`}>
                                            <span className="text-[9px] font-black uppercase tracking-widest text-[#00d4ff]">4. Cargo y Biografía (ES)</span>
                                        </div>
                                        <div className="space-y-2">
                                            <label className={labelClass}>Cargo / Rol (ES) *</label>
                                            <input 
                                                type="text" 
                                                required
                                                placeholder="ej. CEO / Guía de Alta Montaña"
                                                value={formRoleEs}
                                                onChange={e => setFormRoleEs(e.target.value)}
                                                className={inputClass}
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <label className={labelClass}>Biografía (ES) *</label>
                                            <textarea 
                                                required
                                                rows={6}
                                                placeholder="Escribe una biografía detallada en español..."
                                                value={formBioEs}
                                                onChange={e => setFormBioEs(e.target.value)}
                                                className={`${inputClass} resize-none`}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeFormTab === 'en' && (
                                <div className="space-y-6 animate-in fade-in duration-300">
                                    <div className={`p-6 border rounded-[12px] space-y-6 ${
                                        isDark ? 'bg-black/40 border-rock/50 shadow-inner' : 'bg-gray-50 border-gray-200/90 shadow-sm'
                                    }`}>
                                        <div className={`flex items-center gap-2 pb-3 border-b ${
                                            isDark ? 'border-rock/20' : 'border-gray-150/50'
                                        }`}>
                                            <span className="text-[9px] font-black uppercase tracking-widest text-[#00d4ff]">English Profile</span>
                                        </div>
                                        <div className="space-y-2">
                                            <label className={labelClass}>Role / Position (EN) *</label>
                                            <input 
                                                type="text" 
                                                required
                                                placeholder="e.g. CEO / High Mountain Guide"
                                                value={formRoleEn}
                                                onChange={e => setFormRoleEn(e.target.value)}
                                                className={inputClass}
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <label className={labelClass}>Biography (EN) *</label>
                                            <textarea 
                                                required
                                                rows={6}
                                                placeholder="Write a detailed biography in English..."
                                                value={formBioEn}
                                                onChange={e => setFormBioEn(e.target.value)}
                                                className={`${inputClass} resize-none`}
                                            />
                                        </div>
                                    </div>

                                    {/* Section: Mountain Details (EN) */}
                                    <div className={`p-6 border rounded-[12px] space-y-6 ${
                                        isDark ? 'bg-black/40 border-rock/50 shadow-inner' : 'bg-gray-50 border-gray-200/90 shadow-sm'
                                    }`}>
                                        <div className={`flex items-center gap-2 pb-3 border-b ${
                                            isDark ? 'border-rock/20' : 'border-gray-150/50'
                                        }`}>
                                            <span className="text-[9px] font-black uppercase tracking-widest text-[#00d4ff]">Mountain Details (EN)</span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-6">
                                            <div className="space-y-2">
                                                <label className={labelClass}>Focus / Specialty (EN)</label>
                                                <input 
                                                    type="text" 
                                                    placeholder="e.g. Trad crack climbing"
                                                    value={formSpecialtyEn}
                                                    onChange={e => setFormSpecialtyEn(e.target.value)}
                                                    className={inputClass}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label className={labelClass}>Favorite Route (EN)</label>
                                                <input 
                                                    type="text" 
                                                    placeholder="e.g. Cerro Castillo North Face"
                                                    value={formFavoriteRouteEn}
                                                    onChange={e => setFormFavoriteRouteEn(e.target.value)}
                                                    className={inputClass}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeFormTab === 'others' && (
                                <div className="space-y-6 animate-in fade-in duration-300">
                                    <div className={`p-6 border rounded-[12px] space-y-6 ${
                                        isDark ? 'bg-black/40 border-rock/50 shadow-inner' : 'bg-gray-50 border-gray-200/90 shadow-sm'
                                    }`}>
                                        <div className={`flex items-center gap-2 pb-3 border-b ${
                                            isDark ? 'border-rock/20' : 'border-gray-150/50'
                                        }`}>
                                            <span className="text-[9px] font-black uppercase tracking-widest text-[#00d4ff]">Otros Idiomas (Opcional)</span>
                                        </div>
                                        
                                        <button 
                                            type="button"
                                            onClick={() => setShowAdditionalLangs(!showAdditionalLangs)}
                                            className={`w-full flex items-center justify-between p-4 border rounded-[10px] text-xs font-bold uppercase tracking-widest transition-all duration-200 ${
                                                isDark 
                                                ? 'bg-black/20 border-rock hover:bg-black/40 text-gray-300 hover:text-white' 
                                                : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-600 hover:text-gray-900 shadow-sm'
                                            }`}
                                        >
                                            <span>Idiomas Adicionales (PT, FR, IT, DE)</span>
                                            {showAdditionalLangs ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                        </button>

                                        {showAdditionalLangs ? (
                                            <div className={`space-y-6 pl-4 border-l-2 transition-all duration-200 ${isDark ? 'border-rock/40' : 'border-gray-200'}`}>
                                                {/* Portuguese (PT) */}
                                                <div className="space-y-3">
                                                    <span className="text-[9px] font-bold text-[#00d4ff] uppercase tracking-widest font-mono">PORTUGUÊS (PT)</span>
                                                    <input 
                                                        type="text" 
                                                        placeholder="Cargo / Função"
                                                        value={formRolePt}
                                                        onChange={e => setFormRolePt(e.target.value)}
                                                        className={inputClassSm}
                                                    />
                                                    <textarea 
                                                        rows={2}
                                                        placeholder="Biografia"
                                                        value={formBioPt}
                                                        onChange={e => setFormBioPt(e.target.value)}
                                                        className={`${inputClassSm} resize-none`}
                                                    />
                                                </div>

                                                {/* French (FR) */}
                                                <div className="space-y-3">
                                                    <span className="text-[9px] font-bold text-[#00d4ff] uppercase tracking-widest font-mono">FRANÇAIS (FR)</span>
                                                    <input 
                                                        type="text" 
                                                        placeholder="Poste / Rôle"
                                                        value={formRoleFr}
                                                        onChange={e => setFormRoleFr(e.target.value)}
                                                        className={inputClassSm}
                                                    />
                                                    <textarea 
                                                        rows={2}
                                                        placeholder="Biographie"
                                                        value={formBioFr}
                                                        onChange={e => setFormBioFr(e.target.value)}
                                                        className={`${inputClassSm} resize-none`}
                                                    />
                                                </div>

                                                {/* Italian (IT) */}
                                                <div className="space-y-3">
                                                    <span className="text-[9px] font-bold text-[#00d4ff] uppercase tracking-widest font-mono">ITALIANO (IT)</span>
                                                    <input 
                                                        type="text" 
                                                        placeholder="Ruolo / Carica"
                                                        value={formRoleIt}
                                                        onChange={e => setFormRoleIt(e.target.value)}
                                                        className={inputClassSm}
                                                    />
                                                    <textarea 
                                                        rows={2}
                                                        placeholder="Biografia"
                                                        value={formBioIt}
                                                        onChange={e => setFormBioIt(e.target.value)}
                                                        className={`${inputClassSm} resize-none`}
                                                    />
                                                </div>

                                                {/* German (DE) */}
                                                <div className="space-y-3">
                                                    <span className="text-[9px] font-bold text-[#00d4ff] uppercase tracking-widest font-mono">DEUTSCH (DE)</span>
                                                    <input 
                                                        type="text" 
                                                        placeholder="Rolle / Position"
                                                        value={formRoleDe}
                                                        onChange={e => setFormRoleDe(e.target.value)}
                                                        className={inputClassSm}
                                                    />
                                                    <textarea 
                                                        rows={2}
                                                        placeholder="Biografie"
                                                        value={formBioDe}
                                                        onChange={e => setFormBioDe(e.target.value)}
                                                        className={`${inputClassSm} resize-none`}
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <div className={`text-center py-10 text-xs uppercase tracking-widest border border-dashed rounded-[10px] ${
                                                isDark ? 'border-rock/40 text-gray-500' : 'border-gray-200 text-gray-400'
                                            }`}>
                                                Haz clic arriba para desplegar los campos de traducción
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </form>

                        {/* Editor Actions Footer */}
                        <div className={`p-6 border-t flex items-center justify-between shrink-0 ${isDark ? 'border-rock/60 bg-[#1a1512]' : 'border-gray-200 bg-gray-50'}`}>
                            <button 
                                type="button"
                                onClick={() => setIsEditorOpen(false)}
                                className={`px-6 py-4 text-[10px] font-bold uppercase tracking-widest transition-all rounded-[10px] ${
                                    isDark 
                                    ? 'text-gray-400 hover:text-white hover:bg-white/5' 
                                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100 border border-gray-200 shadow-sm'
                                }`}
                            >
                                Cancelar
                            </button>
                            <button 
                                type="submit"
                                form="member-form"
                                onClick={handleSave}
                                className={`px-8 py-4 font-title text-base uppercase tracking-widest transition-all duration-200 shadow-[0_8px_30px_-5px_rgba(0,212,255,0.4)] rounded-[10px] ${
                                    isDark 
                                    ? 'bg-[#00d4ff] text-black hover:bg-white hover:text-black' 
                                    : 'bg-[#00d4ff] text-black hover:bg-gray-900 hover:text-white'
                                }`}
                            >
                                {editingMember ? 'Guardar Cambios' : 'Crear Miembro'}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default TeamManager;
